import type { ReactNode } from "react";
import type { ApiError } from "../api/client";
import { t } from "../i18n/t";
import { severityLabel, statusLabel } from "../lib/format";
import type { FindingStatus } from "../api/client";

export function Loading({ label }: { label?: string }) {
  return (
    <p className="loading" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      {label ?? t("app.loading")}
    </p>
  );
}

/**
 * Page-level loading state that mirrors the dashboard layout, so the page does not jump when the
 * data arrives. Inline fetches keep the smaller <Loading> spinner.
 */
export function PageSkeleton({ label }: { label?: string }) {
  return (
    <div className="page skeleton-page" role="status" aria-live="polite">
      <span className="visually-hidden">{label ?? t("app.loading")}</span>
      <div className="skeleton skeleton-eyebrow" aria-hidden="true" />
      <div className="skeleton skeleton-title" aria-hidden="true" />
      <div className="skeleton skeleton-line" aria-hidden="true" />
      <div className="skeleton-tiles" aria-hidden="true">
        <div className="skeleton skeleton-tile" />
        <div className="skeleton skeleton-tile" />
        <div className="skeleton skeleton-tile" />
      </div>
      <div className="skeleton skeleton-block" aria-hidden="true" />
    </div>
  );
}

export function ErrorMessage({ error, onRetry }: { error: ApiError; onRetry?: () => void }) {
  return (
    <div className="notice notice-error" role="alert">
      <p>{error.userMessage}</p>
      {onRetry && (
        <button type="button" className="btn btn-secondary btn-small" onClick={onRetry}>
          {t("common.retry")}
        </button>
      )}
    </div>
  );
}

type NoticeKind = "info" | "warning" | "error" | "success" | "synthetic";

export function Notice({
  kind = "info",
  title,
  children,
  role,
}: {
  kind?: NoticeKind;
  title?: string;
  children?: ReactNode;
  role?: "status" | "alert" | "note";
}) {
  return (
    <section className={`notice notice-${kind}`} role={role} aria-label={title}>
      {title && <p className="notice-title">{title}</p>}
      {children}
    </section>
  );
}

/** Polite live region for save confirmations and other non-blocking feedback. */
export function LiveMessage({ message, kind = "success" }: { message: string; kind?: NoticeKind }) {
  return (
    <p className={`live-message live-${kind}`} role="status" aria-live="polite">
      {message}
    </p>
  );
}

export function SeverityBadge({ severity }: { severity: string }) {
  return <span className={`badge sev-${severity}`}>{severityLabel(severity)}</span>;
}

export function StatusBadge({ status }: { status: FindingStatus }) {
  return <span className={`badge status-${status}`}>{statusLabel(status)}</span>;
}

export function VisuallyHidden({ children }: { children: ReactNode }) {
  return <span className="visually-hidden">{children}</span>;
}

export function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <nav className="pagination" aria-label={t("common.pagination")}>
      <span>{t("common.showing", { from, to, total })}</span>
      <span className="pagination-buttons">
        <button
          type="button"
          className="btn btn-secondary btn-small"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          {t("common.previous")}
        </button>
        <span>{t("common.pageOf", { page, pages })}</span>
        <button
          type="button"
          className="btn btn-secondary btn-small"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
        >
          {t("common.next")}
        </button>
      </span>
    </nav>
  );
}
